import firestore, {
  FirebaseFirestoreTypes,
} from '@react-native-firebase/firestore';
import {useQuery} from 'react-query';

import {Person, injectPeople, usePeople} from './usePersonInfo';

export interface FundHolder {
  fundId?: FirebaseFirestoreTypes.DocumentReference<Fund>;
  originFundId?: FirebaseFirestoreTypes.DocumentReference<Fund>;
  destinationFundId?: FirebaseFirestoreTypes.DocumentReference<Fund>;
  fund?: Fund;
  [key: string]: unknown;
}

export type ReservationYear = {
  reservationYearId: number;
  name: string;
  startDate: FirebaseFirestoreTypes.Timestamp;
  startJSDate?: Date;
  endDate: FirebaseFirestoreTypes.Timestamp;
  endJSDate?: Date;
  ref: FirebaseFirestoreTypes.DocumentReference<ReservationYear>;
};

export type Fund = {
  fund: string;
  fundCode: string;
  fundId: number;
  internal: boolean;
  acceptingReservations: boolean;
  guestyCreditCustomFieldName?: string;
  guestyCreditRefundCustomFieldName?: string;
  creditsPerUnit: number; // FIXME are there ranges ? or straight multiply
  ref: FirebaseFirestoreTypes.DocumentReference<Fund>;
};

export type CreditTypeHolder = {
  creditTypeId?: FirebaseFirestoreTypes.DocumentReference<CreditType>;
  creditType?: CreditType;
  [key: string]: unknown;
};

export type CreditType = {
  creditType: string;
  creditTypeId: number;
  expirationYear?: FirebaseFirestoreTypes.Timestamp;
  fund?: Fund;
  fundId: FirebaseFirestoreTypes.DocumentReference<Fund>;
  allowReservation: boolean;
  allowAFO: boolean;
  allowCD: boolean;
  allowECC: boolean;
  allowFCC: boolean;
  reservationYearId: FirebaseFirestoreTypes.DocumentReference<ReservationYear>;
  ref: FirebaseFirestoreTypes.DocumentReference<CreditType>;
};

export type Property = {
  propertyName: string;
  propertyId: number;
  propertyUrl: string;
  propertyNotes?: string;
  ref: FirebaseFirestoreTypes.DocumentReference<Property>;
  active: boolean;
  fundId: FirebaseFirestoreTypes.DocumentReference<Fund>;
  fund?: Fund;
  guestyId?: string;
};

export type ExchangeRate = {
  dateRangeEnd: FirebaseFirestoreTypes.Timestamp;
  dateRangeEndDate?: Date;
  dateRangeStart: FirebaseFirestoreTypes.Timestamp;
  dateRangeStartDate?: Date;
  destinationFundId: FirebaseFirestoreTypes.DocumentReference<Fund>;
  destinationFund?: Fund;
  originFundId: FirebaseFirestoreTypes.DocumentReference<Fund>;
  originFund?: Fund;
  exchangeRate: string;
  exchangeRateId: number;
  ref: FirebaseFirestoreTypes.DocumentReference<ExchangeRate>;
};

export interface LimitedPartnersHolder {
  limitedPartnerIds?: FirebaseFirestoreTypes.DocumentReference<LimitedPartner>[];
  limitedPartners?: LimitedPartner[];
  limitedPartnerId?: FirebaseFirestoreTypes.DocumentReference<LimitedPartner>;
  limitedPartner?: LimitedPartner;
}

export type Subscriber = {
  partnerNumber: number;
  subscriberId: number;
  subscriberName: string;
  subscriberInvestmentDate?: FirebaseFirestoreTypes.Timestamp;
  subscriberInvestmentJSDate?: Date;
  subscriberDivestmentDate?: FirebaseFirestoreTypes.Timestamp;
  subscriberDivestmentJSDate?: Date;
  subscriberNotes?: string;
  subscriberUnits: string; // FIXME maybe could parse this out? It's like a currency unit though, not IEEE
  ref: FirebaseFirestoreTypes.DocumentReference<Subscriber>;
  fundId: FirebaseFirestoreTypes.DocumentReference<Fund>;
  fund?: Fund;
  limitedPartnerIds?: FirebaseFirestoreTypes.DocumentReference<LimitedPartner>[];
  limitedPartners?: LimitedPartner[];
};

export interface MembersHolder {
  memberIds: FirebaseFirestoreTypes.DocumentReference<Member>[];
  members?: Member[];
}

export type LimitedPartner = {
  limitedPartnerId: number;
  limitedPartnerName: string;
  limitedPartnerNotes?: string;
  limitedPartnerUnits?: number;
  taxIdNumber: number;
  annualFee?: string;
  annualFeeWaived?: boolean;
  eliteAllianceComplimentary?: boolean;
  eliteAllianceComplimentaryExpiration?: FirebaseFirestoreTypes.Timestamp;
  thirdHomeComplimentary?: boolean;
  thirdHomeComplimentaryExpiration?: FirebaseFirestoreTypes.Timestamp;
  memberIds: FirebaseFirestoreTypes.DocumentReference<Member>[];
  guestyGuestIds?: string[];
  guestyReservationIds?: string[];

  // ephemeral - delete from object before saving
  ref: FirebaseFirestoreTypes.DocumentReference<LimitedPartner>;
  members?: Member[];
};

export type Member = {
  memberId: number;
  memberName: string;
  memberNotes?: string;
  ref: FirebaseFirestoreTypes.DocumentReference<Member>;
  personIds: FirebaseFirestoreTypes.DocumentReference<Person>[];
  people: Person[];
};

export interface PropertyHolder {
  propertyId: FirebaseFirestoreTypes.DocumentReference<Property>;
  property?: Property;
}

export type Reservation = {
  limitedPartnerId?: FirebaseFirestoreTypes.DocumentReference<LimitedPartner>;
  limitedPartner?: LimitedPartner;
  propertyId: FirebaseFirestoreTypes.DocumentReference<Property>;
  property?: Property;
  requiredCredits: string;
  status?: string;
  reservationCreatedDate?: FirebaseFirestoreTypes.Timestamp;
  reservationCreatedJSDate?: Date;
  reservationEndDate: FirebaseFirestoreTypes.Timestamp;
  reservationEndJSDate?: Date;
  reservationStartDate: FirebaseFirestoreTypes.Timestamp;
  reservationStartJSDate?: Date;
  reservationId: number;
  ref: FirebaseFirestoreTypes.DocumentReference<Reservation>;
  guestyReservationId?: string;

  guestyTotalFees: number; // unformatted dollar value
  guestyBalanceDue: number; // unformatted dollar value
  guestyPaymentsDue: number; // unformatted dollar value
};

export type CreditTransaction = {
  allocationDate: FirebaseFirestoreTypes.Timestamp | Date;
  allocationDateJS?: Date;
  creditTransactionId: number;
  creditTypeId: FirebaseFirestoreTypes.DocumentReference<CreditType>;
  creditType?: CreditType;
  credits: string;
  expirationDate?: FirebaseFirestoreTypes.Timestamp;
  expirationDateJS?: Date;
  limitedPartnerId: FirebaseFirestoreTypes.DocumentReference<LimitedPartner>;
  limitedPartner?: LimitedPartner;
  reservationId?: FirebaseFirestoreTypes.DocumentReference<Reservation>;
  transactionType: string;
  managedAutomatically?: boolean; // FIXME add to editor
  notes?: string;
  ref: FirebaseFirestoreTypes.DocumentReference<CreditTransaction>;
};

export enum CreditTransactionType {
  AnnualAllocation = 'Annual Allocation',
  AnnualFeeOffset = 'Annual Fee Offset',
  // FIXME we can't have this be per year really
  CapitalDividend = 'Credits Used for Capital Dividend',
  CreditPurchase = 'Extra Credits Purchased',
  Reservation = 'Reservation',
  ReferralCredited = 'Referral Bonus Credited',
  ManualAdjustment = 'Manual Adjustment',
}

export function creditExchangeTransactionType(fundCode: string): string {
  return `Credit Exchange - ${fundCode}`;
}

export function getCreditTransactionTypeSelectItems(funds?: Fund[]): string[] {
  const staticTypes = Object.values(CreditTransactionType).map(type =>
    type.toString(),
  );
  const exchangeTypes = (funds ?? [])
    .slice()
    .sort((a, b) => a.fundCode.localeCompare(b.fundCode))
    .map(fund => creditExchangeTransactionType(fund.fundCode));
  const manualIdx = staticTypes.indexOf(CreditTransactionType.ManualAdjustment);
  if (manualIdx === -1) {
    return [...staticTypes, ...exchangeTypes];
  }
  return [
    ...staticTypes.slice(0, manualIdx),
    ...exchangeTypes,
    ...staticTypes.slice(manualIdx),
  ];
}

async function fetchFunds(): Promise<Fund[] | undefined> {
  const snap = await firestore()
    .collection('funds')
    .orderBy('internal', 'desc')
    .get();
  return snap.docs.map(doc => {
    const data = doc.data() as Fund;
    data.ref = doc.ref as FirebaseFirestoreTypes.DocumentReference<Fund>;
    return data;
  });
}

export function useFunds(): Fund[] | undefined {
  const results = useQuery('funds', fetchFunds);
  if (results.isLoading || results.isError || results.data === undefined) {
    return;
  }
  return results.data;
}

async function fetchReservationYears(): Promise<ReservationYear[] | undefined> {
  const snap = await firestore()
    .collection('reservationYears')
    .orderBy('startDate', 'desc')
    .get();
  return snap.docs.map(doc => {
    const data = doc.data() as ReservationYear;
    data.ref =
      doc.ref as FirebaseFirestoreTypes.DocumentReference<ReservationYear>;
    data.endJSDate = data.endDate.toDate();
    data.startJSDate = data.startDate.toDate();
    return data;
  });
}

export function useReservationYears(): ReservationYear[] | undefined {
  const results = useQuery('reservationYears', fetchReservationYears);
  if (results.isLoading || results.isError || results.data === undefined) {
    return;
  }
  return results.data;
}

async function fetchExchangeRates(): Promise<ExchangeRate[] | undefined> {
  const snap = await firestore()
    .collection('exchangeRates')
    .orderBy('dateRangeStart')
    .orderBy('originFundId')
    .get();
  return snap.docs.map(doc => {
    const data = doc.data() as ExchangeRate;
    data.ref =
      doc.ref as FirebaseFirestoreTypes.DocumentReference<ExchangeRate>;
    data.dateRangeStartDate = data.dateRangeStart?.toDate() ?? undefined;
    data.dateRangeEndDate = data.dateRangeEnd?.toDate() ?? undefined;
    return data;
  });
}

export function useExchangeRates(): ExchangeRate[] | undefined {
  // All hooks first so hook count is same every render (otherwise React throws an error)
  const queryResult = useQuery('exchangeRates', fetchExchangeRates);
  const funds = useFunds();
  const exchangeRates = queryResult.data ?? [];
  injectFunds(exchangeRates, funds ?? [], 'originFund', 'originFundId');
  injectFunds(
    exchangeRates,
    funds ?? [],
    'destinationFund',
    'destinationFundId',
  );
  if (queryResult.isLoading || queryResult.isError) {
    return;
  }
  return exchangeRates as ExchangeRate[];
}

async function fetchCreditTypes(): Promise<CreditType[] | undefined> {
  const snap = await firestore()
    .collection('creditTypes')
    .orderBy('fundId')
    .orderBy('creditType')
    .get();
  return snap.docs.map(doc => {
    const data = doc.data() as CreditType;
    data.ref = doc.ref as FirebaseFirestoreTypes.DocumentReference<CreditType>;
    return data;
  });
}

export function useCreditTypes(): CreditType[] | undefined {
  // All hooks first so hook count is same every render (otherwise React throws an error)
  const queryResult = useQuery('creditTypes', fetchCreditTypes);
  const funds = useFunds();
  const creditTypes = queryResult.data ?? [];
  injectFunds(creditTypes, funds ?? []);
  if (queryResult.isLoading || queryResult.isError) {
    return;
  }
  return creditTypes as CreditType[];
}

async function fetchProperties(): Promise<Property[] | undefined> {
  const snapshot = await firestore()
    .collection('properties')
    .orderBy('active', 'desc')
    .orderBy('propertyName')
    .get();
  return snapshot.docs.map(doc => {
    const data = doc.data() as Property;
    data.ref = doc.ref as FirebaseFirestoreTypes.DocumentReference<Property>;
    return data as Property;
  });
}

export function useProperties(): Property[] | undefined {
  // All hooks first so hook count is same every render (otherwise React throws an error)
  const funds = useFunds();
  const queryResult = useQuery('properties', fetchProperties);
  if (
    queryResult.isLoading ||
    queryResult.isError ||
    funds === undefined ||
    queryResult.data === undefined
  ) {
    return;
  }
  const properties = queryResult.data;
  injectFunds(properties, funds);
  return properties as Property[];
}

async function fetchSubscribers(): Promise<Subscriber[] | undefined> {
  const snapshot = await firestore()
    .collection('subscribers')
    // .where('subscriberId', '==', 47) // FIXME remove this for full scan
    .orderBy('subscriberName')
    .get();
  return snapshot.docs.map(doc => {
    const data = doc.data() as Subscriber;
    data.ref = doc.ref as FirebaseFirestoreTypes.DocumentReference<Subscriber>;
    return data as Subscriber;
  });
}

export const minimumTextSearchString = 3;
export const minimumNumberSearchString = 1;

const isNumeric = (num: any) =>
  (typeof num === 'number' || (typeof num === 'string' && num.trim() !== '')) &&
  !isNaN(num as number);

export function shouldSearch(searchString: string) {
  if (
    searchString.length >= minimumTextSearchString ||
    (isNumeric(searchString) &&
      searchString.length >= minimumNumberSearchString)
  ) {
    return true;
  }
  return false;
}

export function searchLimitedPartners(
  search: string,
  fund: Fund | undefined,
  limitedPartners?: ReadonlyArray<LimitedPartner>,
  limitedPartnerFundMap?: {[key: string]: Fund},
): LimitedPartner[] | undefined {
  if (limitedPartners === undefined || !shouldSearch(search)) {
    return undefined;
  }
  return limitedPartners.filter(lp => {
    if (
      fund !== undefined &&
      limitedPartnerFundMap !== undefined &&
      limitedPartnerFundMap[lp.ref.id].fundCode !== fund.fundCode
    ) {
      // console.log(`lp ${lp.limitedPartnerName} not in fund ${fund.fundCode} ?`);
      return false;
    }
    if (
      lp.limitedPartnerName.toLowerCase().includes(search) ||
      (lp.taxIdNumber + '').includes(search)
    ) {
      return true;
    }
    for (const member of lp.members ?? []) {
      if (member.memberName.toLowerCase().includes(search)) {
        return true;
      }
      for (const person of member.people ?? []) {
        if (
          person.email.toLowerCase().includes(search) ||
          person.emailSecondary?.toLowerCase().includes(search) ||
          person.firstName.toLowerCase().includes(search) ||
          person.lastName.toLowerCase().includes(search) ||
          person.phoneNumber1?.toLowerCase().includes(search) ||
          person.phoneNumber2?.toLowerCase().includes(search)
        ) {
          return true;
        }
      }
    }

    return false;
  });
}

async function fetchLimitedPartners(): Promise<LimitedPartner[] | undefined> {
  const snapshot = await firestore()
    .collection('limitedPartners')
    // .where('limitedPartnerId', 'in', [48, 49]) // FIXME remove this for full scan
    .orderBy('limitedPartnerName')
    .get();
  return snapshot.docs.map(doc => {
    const data = doc.data() as LimitedPartner;
    data.ref =
      doc.ref as FirebaseFirestoreTypes.DocumentReference<LimitedPartner>;
    // console.log('fetchLimitedPartners w/partner: ' + data.limitedPartnerName);
    return data as LimitedPartner;
  });
}

export function useFetchLimitedPartners(): LimitedPartner[] | undefined {
  const queryResult = useQuery('limitedPartners', fetchLimitedPartners);
  // if (queryResult.isError) {
  //   console.log('what happened? ' + queryResult.error);
  // }
  if (queryResult.isLoading || queryResult.isError) {
    return;
  }
  return queryResult.data as LimitedPartner[];
}

export function filterSubscribers(
  subscribers?: ReadonlyArray<Subscriber>,
  limitedPartners?: ReadonlyArray<LimitedPartner>,
  members?: ReadonlyArray<Member>,
  peopleIds?: number[],
): Subscriber[] | undefined {
  const filteredSubscribers: Set<Subscriber> = new Set();
  if (!peopleIds || !members || !limitedPartners || !subscribers) {
    // console.warn('missing parameters, cannot filter subscribors');
    return undefined;
  }

  const filteredLimitedPartners = filterLimitedPartners(
    limitedPartners,
    members,
    peopleIds,
  );

  subscribers.map(subscriber => {
    const lps: Set<LimitedPartner> = new Set();
    const lpIds: Set<FirebaseFirestoreTypes.DocumentReference<LimitedPartner>> =
      new Set();
    subscriber.limitedPartners?.map(subLp => {
      filteredLimitedPartners.map(lp => {
        if (lp.limitedPartnerId === subLp.limitedPartnerId) {
          // console.log('found subLP: ' + subLp.limitedPartnerId);
          lps.add(lp);
          lpIds.add(lp.ref);
        }
      });
    });
    if (lps.size > 0) {
      const mutableSub = {...subscriber};
      mutableSub.limitedPartners = Array.from(lps);
      mutableSub.limitedPartnerIds = Array.from(lpIds);
      filteredSubscribers.add(mutableSub);
    }
  });

  return Array.from(filteredSubscribers);
}

export function filterLimitedPartners(
  limitedPartners?: ReadonlyArray<LimitedPartner>,
  members?: ReadonlyArray<Member>,
  peopleIds?: number[],
): LimitedPartner[] {
  const filteredLimitedPartners: Set<LimitedPartner> = new Set();
  if (!peopleIds || !members || !limitedPartners) {
    // console.warn('missing parameters, cannot filter limited partners');
    return Array.from(filteredLimitedPartners);
  }

  // Only keep the members that contain the given person
  const filteredMembers = filterMembers(members, peopleIds);

  limitedPartners.map(lp => {
    const lpMembers: Set<Member> = new Set();
    const lpMemberIds: Set<FirebaseFirestoreTypes.DocumentReference<Member>> =
      new Set();
    lp.members?.map(lpmember => {
      filteredMembers.map(member => {
        if (member.memberId === lpmember.memberId) {
          // console.log('got a limited partner');
          lpMembers.add(member);
          lpMemberIds.add(member.ref);
        }
      });
    });
    if (lpMembers.size > 0) {
      const mutableLp = {...lp};
      mutableLp.members = Array.from(lpMembers);
      mutableLp.memberIds = Array.from(lpMemberIds);
      filteredLimitedPartners.add(mutableLp);
    }
  });

  return Array.from(filteredLimitedPartners);
}

export function filterMembers(
  members?: ReadonlyArray<Member>,
  peopleIds?: number[],
): Member[] {
  // Only keep the members that contain the given person
  const filteredMembers: Set<Member> = new Set();
  if (!peopleIds || !members) {
    // console.warn('missing parameters, cannot filter members');
    return Array.from(filteredMembers);
  }
  // console.log(
  //   'filtering ' +
  //     members.length +
  //     ' members against ' +
  //     peopleIds.length +
  //     ' peopleIds',
  // );
  // console.warn('peopleIds are ' + peopleIds.join(', '));

  // FIXME check members with multiple contacts when we have them
  members.map(member => {
    member.people?.map(memberperson => {
      peopleIds.map(personId => {
        // FIXME the string coercion is the stupidest...
        if (personId + '' === memberperson.personId + '') {
          // console.log(' !!! found myself');
          filteredMembers.add(member);
        }
      });
    });
  });
  // console.log('filteredMembers size? ' + filteredMembers.length);
  return Array.from(filteredMembers);
}

async function fetchMembers(): Promise<Member[] | undefined> {
  const snapshot = await firestore()
    .collection('members')
    .orderBy('memberName')
    // .where('memberId', 'in', [48, 49]) // FIXME remove this for full scan
    .get();
  return snapshot.docs.map(doc => {
    const data = doc.data() as Member;
    if (data.personIds === undefined) {
      data.personIds = [];
    }
    data.ref = doc.ref as FirebaseFirestoreTypes.DocumentReference<Member>;
    return data as Member;
  });
}

export function useMembers(): ReadonlyArray<Member> | undefined {
  const people = usePeople();
  const queryResult = useQuery('members', fetchMembers);
  // console.log('got members? ' + queryResult.data?.length);
  if (
    queryResult.isLoading ||
    queryResult.isError ||
    queryResult.data === undefined ||
    people === undefined
  ) {
    return;
  }
  const members = queryResult.data;
  injectPeople(members, people);
  return members as Member[] as ReadonlyArray<Member>;
}

export function useFetchMembers(): ReadonlyArray<Member> | undefined {
  const queryResult = useQuery('members', fetchMembers);
  if (
    queryResult.isLoading ||
    queryResult.isError ||
    queryResult.data === undefined
  ) {
    return;
  }
  return queryResult.data as Member[] as ReadonlyArray<Member>;
}

async function fetchReservations(): Promise<Reservation[] | undefined> {
  const snapshot = await firestore()
    .collection('reservations')
    // .where('memberId', 'in', [48, 49]) // FIXME remove this for full scan
    .orderBy('reservationStartDate')
    .get();
  return snapshot.docs.map(doc => {
    const data = doc.data() as Reservation;
    data.ref = doc.ref as FirebaseFirestoreTypes.DocumentReference<Reservation>;
    data.reservationEndJSDate = data.reservationEndDate?.toDate() ?? undefined;
    data.reservationStartJSDate =
      data.reservationStartDate?.toDate() ?? undefined;
    return data as Reservation;
  });
}

export function useReservations(): Reservation[] | undefined {
  const reservationsQuery = useQuery('reservations', fetchReservations);
  const limitedPartners = useFetchLimitedPartners();
  const members = useMembers();
  const people = usePeople();
  const properties = useProperties();

  if (
    reservationsQuery.isLoading ||
    reservationsQuery.isError ||
    reservationsQuery.data === undefined ||
    limitedPartners === undefined ||
    members === undefined ||
    people === undefined ||
    properties === undefined
  ) {
    return;
  }

  const reservations = reservationsQuery.data;
  injectLimitedPartners(reservations, limitedPartners, members, people);
  injectProperties(reservations, properties);
  return reservations as Reservation[];
}

/**
 * Account screens keep the reservation year that contains `now` and the year
 * immediately before it. `useReservationYears` is start-date descending and
 * years overlap, so the current year is the newest range that contains `now`.
 */
export function reservationYearsShownInAccount(
  reservationYears: ReadonlyArray<ReservationYear> | undefined,
  now = new Date(),
): {current?: ReservationYear; previous?: ReservationYear} {
  if (!reservationYears || reservationYears.length === 0) {
    return {};
  }
  const currentIndex = reservationYears.findIndex(year => {
    if (!year.startJSDate || !year.endJSDate) {
      return false;
    }
    return year.startJSDate <= now && now <= year.endJSDate;
  });
  const index = currentIndex >= 0 ? currentIndex : 0;
  return {
    current: reservationYears[index],
    previous: reservationYears[index + 1],
  };
}

function expiredCreditTypeKeptForPreviousYear(
  creditType: CreditType | undefined,
  reservationYears: ReadonlyArray<ReservationYear> | undefined,
): boolean {
  const {previous} = reservationYearsShownInAccount(reservationYears);
  if (!previous || !creditType) {
    return false;
  }
  if (creditType.reservationYearId && previous.ref) {
    return creditType.reservationYearId.path === previous.ref.path;
  }
  const expiration = creditType.expirationYear?.toDate();
  if (!expiration || !previous.startJSDate || !previous.endJSDate) {
    return false;
  }
  return expiration >= previous.startJSDate && expiration <= previous.endJSDate;
}

export function filterCreditTransactions(
  txns: ReadonlyArray<CreditTransaction>,
  creditTypes: ReadonlyArray<CreditType>,
  lps: ReadonlyArray<LimitedPartner>,
  members: ReadonlyArray<Member>,
  people: ReadonlyArray<Person>,
  reservationYears?: ReadonlyArray<ReservationYear>,
): ReadonlyArray<CreditTransaction> | undefined {
  const filteredTxns: Set<CreditTransaction> = new Set();
  if (!txns || !lps || !members || !people) {
    return undefined;
  }

  injectCreditTypes(txns, creditTypes);

  // console.log('sifting through ' + txns.length + ' txns');
  txns.forEach(txn => {
    // Drop expired credit types except the previous reservation year, so
    // last year's history stays on the account and older years stay off.
    if (
      txn.creditType?.expirationYear &&
      txn.creditType.expirationYear.toDate() < new Date() &&
      !expiredCreditTypeKeptForPreviousYear(txn.creditType, reservationYears)
    ) {
      // console.log('CreditTransaction is for type that has expired. Skipping.');
      return;
    }

    lps.forEach(lp => {
      // console.log(
      //   'txn side / lp side: ' + txn.limitedPartnerId.path + '/' + lp.ref.path,
      // );
      if (lp.ref.path === txn.limitedPartnerId.path) {
        filteredTxns.add(txn);
      }
    });
  });

  const injectedTxns = Array.from(filteredTxns);
  injectLimitedPartners(injectedTxns, lps, members, people);
  return injectedTxns;
}

export function filterReservations(
  reservations?: Reservation[],
  lps?: ReadonlyArray<LimitedPartner>,
  members?: ReadonlyArray<Member>,
  people?: ReadonlyArray<Person>,
  reservationYears?: ReadonlyArray<ReservationYear>,
): ReadonlyArray<Reservation> | undefined {
  const filteredReservations: Set<Reservation> = new Set();
  if (!reservations || !lps || !members || !people) {
    return undefined;
  }

  // console.log('sifting through ' + reservations.length + ' reservations');
  const {previous} = reservationYearsShownInAccount(reservationYears);
  reservations.forEach(res => {
    // Hide stays that started before the previous reservation year.
    if (
      previous?.startJSDate &&
      res.reservationStartDate.toDate() < previous.startJSDate
    ) {
      return;
    }

    lps.forEach(lp => {
      // console.log(
      //   'res side / lp side: ' + res.limitedPartnerId?.path + '/' + lp.ref.path,
      // );
      if (lp.ref.path === res.limitedPartnerId?.path) {
        // console.log('LP ' + lp.limitedPartnerId + ' in the txn');
        filteredReservations.add(res);
      }
    });
  });

  const filteredReservationsArray = Array.from(filteredReservations);
  injectLimitedPartners(filteredReservationsArray, lps, members, people);
  return filteredReservationsArray;
}

async function fetchCreditTransactions(): Promise<
  CreditTransaction[] | undefined
> {
  const snapshot = await firestore()
    .collection('creditTransactions')
    // .where('memberId', 'in', [48, 49]) // FIXME remove this for full scan
    .orderBy('allocationDate', 'desc')
    .get();
  return snapshot.docs.map(doc => {
    const data = doc.data() as CreditTransaction;
    data.ref =
      doc.ref as FirebaseFirestoreTypes.DocumentReference<CreditTransaction>;
    data.allocationDateJS =
      (data.allocationDate as FirebaseFirestoreTypes.Timestamp)?.toDate() ??
      undefined;
    data.expirationDateJS = data.expirationDate?.toDate() ?? undefined;
    return data as CreditTransaction;
  });
}

export function useCreditTransactions():
  | ReadonlyArray<CreditTransaction>
  | undefined {
  const lps = useFetchLimitedPartners();
  const members = useFetchMembers();
  const people = usePeople();
  const creditTypes = useCreditTypes();
  const queryResult = useQuery('creditTransactions', fetchCreditTransactions);
  if (
    queryResult.isLoading ||
    queryResult.isError ||
    queryResult.data === undefined ||
    lps === undefined ||
    members === undefined ||
    people === undefined ||
    creditTypes === undefined
  ) {
    return;
  }

  // FIXME filter either here or in the fetch for:
  // - txns that are not priority visit annual types... OR
  // - txns that are for current reservation year

  // alternatively: do expiration correctly and only non-expired ones

  let txns = queryResult.data;
  injectLimitedPartners(txns, lps, members, people);
  injectCreditTypes(txns as ReadonlyArray<CreditTypeHolder>, creditTypes);
  return txns as ReadonlyArray<CreditTransaction>;
}

export function useFetchSubscribers(): ReadonlyArray<Subscriber> | undefined {
  const subsQuery = useQuery('subscribers', fetchSubscribers);
  const funds = useFunds();
  if (
    subsQuery.isLoading ||
    subsQuery.isError ||
    subsQuery.data === undefined ||
    funds === undefined
  ) {
    return;
  }
  const subs = subsQuery.data;
  injectFunds(subs, funds);
  return subs;
}

export function useSubscribers(): ReadonlyArray<Subscriber> | undefined {
  // All hooks first so hook count is same every render (otherwise React throws an error)
  const subsQuery = useQuery('subscribers', fetchSubscribers);
  const lps = useFetchLimitedPartners();
  const members = useFetchMembers();
  const people = usePeople();
  const funds = useFunds();
  if (subsQuery.isError || subsQuery.isLoading) {
    return;
  }
  if (
    lps === undefined ||
    members === undefined ||
    people === undefined ||
    funds === undefined ||
    subsQuery.data === undefined
  ) {
    return;
  }

  let subs = subsQuery.data;
  injectFunds(subs, funds);
  injectLimitedPartners(subs, lps, members, people);
  return subs as ReadonlyArray<Subscriber>;
}

function injectFunds(
  holders: ReadonlyArray<FundHolder>,
  funds: ReadonlyArray<Fund>,
  targetPropertyName?: string,
  sourcePropertyName?: string,
) {
  const sourceProp = sourcePropertyName ?? 'fundId';
  const targetProp = targetPropertyName ?? 'fund';
  holders.forEach(holder => {
    holder[targetProp] = funds.find(fund =>
      fund.ref.path ===
      (holder[sourceProp] as FirebaseFirestoreTypes.DocumentReference).path
        ? true
        : false,
    );
  });
}

function injectProperties(holders: PropertyHolder[], properties: Property[]) {
  holders.forEach(holder => {
    holder.property = properties.find(property =>
      property.ref.path === holder.propertyId.path ? true : false,
    );
  });
}

export function injectLimitedPartners(
  holders: ReadonlyArray<LimitedPartnersHolder>,
  limitedPartners: ReadonlyArray<LimitedPartner>,
  members: ReadonlyArray<Member>,
  people: ReadonlyArray<Person>,
) {
  holders.forEach(holder => {
    // Process the id array if they have one
    holder.limitedPartners = [];
    holder.limitedPartnerIds?.forEach(heldRef => {
      if (holder.limitedPartners === undefined) {
        holder.limitedPartners = [];
      }
      const match = limitedPartners?.find(lp =>
        lp.ref.path === heldRef.path ? true : false,
      );
      if (match !== undefined) {
        injectMembers([match], members, people);
        holder.limitedPartners.push(match);
      }
    });

    // get the single one if there is only one
    if (holder.limitedPartnerId !== undefined) {
      let limitedPartner = limitedPartners.find(lp =>
        lp.ref.path === holder.limitedPartnerId?.path ? true : false,
      );
      if (limitedPartner !== undefined) {
        injectMembers([limitedPartner], members, people);
      }
      holder.limitedPartner = limitedPartner;
    }
  });
}

export function injectMembers(
  holders: MembersHolder[],
  members: ReadonlyArray<Member>,
  people: ReadonlyArray<Person>,
) {
  holders.forEach(holder => {
    holder.members = [];
    holder.memberIds?.forEach(heldRef => {
      if (holder.members === undefined) {
        holder.members = [];
      }
      const match = members.find(member =>
        member.ref.path === heldRef.path ? true : false,
      );
      if (match !== undefined) {
        injectPeople([match], people);
        holder.members.push(match);
      }
    });
  });
}

export function injectCreditTypes(
  holders: ReadonlyArray<CreditTypeHolder>,
  creditTypes: ReadonlyArray<CreditType>,
) {
  holders.forEach(holder => {
    const creditType = creditTypes.find(
      type => type.ref.path === holder.creditTypeId?.path,
    );
    if (creditType !== undefined) {
      holder.creditType = creditType;
    }
  });
}
